// src/pages/instances/tables/InstancesTable.tsx
import { useTranslation } from "react-i18next";
import Table from "../../../components/Common/dataTable";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { Instance } from "types/superAdmin/instanceTypes";
import { formatToLocaleDate } from "utils/dateUtils";
import Badge from "components/Common/ui/Badge";
import { showConfirmDialog } from "utils/confirmDialogUtils";

interface InstancesTableProps {
  dataSource: Instance[];
  loading?: boolean;
  onEdit: (record: Instance) => void;
  onShowDirectors: (record: Instance) => void;
  onAddDirector?: (record: Instance) => void;
  pagination?: boolean;
  dataPaginate?: {
    current: number;
    pageSize: number;
    total: number;
  };
  onChange?: (pagination: any) => void;
  onDelete?: (record: Instance) => void;
}

const InstancesTable: React.FC<InstancesTableProps> = ({
  dataSource,
  loading,
  onEdit,
  onShowDirectors,
  onAddDirector,
  pagination,
  dataPaginate,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation();

  const handleDelete = (record: Instance) => {
    if (!onDelete) {
      console.warn('onDelete function is not provided');
      return;
    }

    showConfirmDialog({
      title: t("delete_instance"),
      content: t("are_you_sure_you_want_to_delete_this_instance"),
      okText: t("yes"),
      cancelText: t("no"),
      okType: "danger",
      loading: false,
      onOk: () => onDelete(record),
    });
  };

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: (a: Instance, b: Instance) => a.name.localeCompare(b.name),
    },
    {
      title: t("phone_number"),
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (v: string) => v || "—",
    },
    {
      title: t("address"),
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (v: Instance["status"]) => (v === "active" ? <Badge variant="success" text={t("active")} /> : <Badge variant="danger" text={t("deleted")} />),
    },
    {
      title: t("certificate.form.directorName.label"),
      dataIndex: "directorName",
      key: "directorName",
      render: (v: string) => v || "—",
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: Instance, b: Instance) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (v: string) => formatToLocaleDate(v),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: Instance) => {

        const actions: Action[] = [
          {
            label: t("edit"),
            icon: "ti ti-edit",
            actionType: "link",
            onClick: () => onEdit(record),
          },
          {
            label: t("delete"),
            icon: "ti ti-trash",
            actionType: "link",
            onClick: () => handleDelete(record),
          },
          {
            label: t("show_list_directors"),
            icon: "ti ti-users",
            actionType: "link",
            onClick: () => onShowDirectors(record),
          },
          ...(onAddDirector
            ? [{
                label: t("add_director"),
                icon: "ti ti-user-plus",
                actionType: "link" as const,
                onClick: () => onAddDirector(record),
              }]
            : []),
        ];
        return record.status === "active" ? <ActionsDropdown actions={actions} /> : <i className="ti ti-lock"></i>;
      },
    },
  ];

  return (
    <div className="mt-4">
      <div className="table-responsive">
        <Table columns={columns} dataSource={dataSource} loading={loading} pagination={pagination}
          dataPaginate={dataPaginate}
          onChange={onChange} />
      </div>
    </div>
  );
};

export default InstancesTable;
